import { useState, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import { TimekeeperService } from "services/TimekeeperService";
import type { AccrualProfile, AccrualProfileRow } from "repository/timekeeper/@types/AccrualProfile";

const timekeeperService = new TimekeeperService();

type UseAccrualProfileDataProps = {
    employeeId: number;
    open: boolean;
    onLoaded: (rows: AccrualProfileRow[]) => void;
};

export const useAccrualProfileData = ({ employeeId, open, onLoaded }: UseAccrualProfileDataProps) => {
    const [availableProfiles, setAvailableProfiles] = useState<AccrualProfile[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const loadData = useCallback(async () => {
        if (!employeeId) return;
        setLoading(true);
        try {
            const [existingResult, allProfilesResult] = await Promise.allSettled([
                timekeeperService.employee_accrual_profile.getAll(employeeId),
                timekeeperService.accrual_profile.getAll(),
            ]);

            if (allProfilesResult.status === "fulfilled") {
                setAvailableProfiles(allProfilesResult.value as AccrualProfile[]);
            }

            if (existingResult.status === "fulfilled") {
                const existing = existingResult.value || [];
                const rows: AccrualProfileRow[] = existing.map((p, i) => ({
                    id: i + 1,
                    employee_accrual_profile_id: p.accrual_profile_r_employee_id,
                    employee_id: p.employee_id,
                    accrual_profile_id: p.accrual_profile_id,
                    start_date: dayjs(p.start_date),
                    end_date: dayjs(p.end_date),
                }));
                onLoaded(rows);
            }
        } catch (error) {
            console.error("Error loading accrual profiles:", error);
        } finally {
            setLoading(false);
        }
    }, [employeeId, onLoaded]);

    useEffect(() => {
        if (open && employeeId) loadData();
    }, [open, employeeId,loadData]);

    return { availableProfiles, loading };
}